import { useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Button, List, Stack, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { NotificationSetting } from 'features/notifications/notifications.types';
import { getSortedRegionsSections } from 'shared/utils';
import { NotificationTypes } from 'features/notifications/notifications.constants';
import { RegionCard } from './RegionCard/RegionCard';

type CountyNotificationsProps = {
  regionSettings: NotificationSetting[];
};

const useStyles = makeStyles()((theme) => ({
  card: {
    width: '100%',
    flex: 1,
    backgroundColor: theme.palette.background.secondary,
    color: theme.palette.text.primary,
    borderRadius: Number(theme.shape.borderRadius) * 2.7,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
}));

const getActive = (
  regionSettings: NotificationSetting[],
): NotificationSetting[] =>
  regionSettings.filter(
    (regionSetting) => regionSetting.setting !== NotificationTypes.OFF.key,
  );

export const CountyNotifications = (
  props: CountyNotificationsProps,
): JSX.Element => {
  const { regionSettings } = props;
  const { t } = useTranslation();
  const { classes } = useStyles();
  const history = useHistory();
  const activeSettings = getActive(regionSettings);

  const regionSettingsSections = useMemo(
    () => getSortedRegionsSections(activeSettings, null),
    [activeSettings],
  );

  const handleAddMoreCounties = (): void => {
    history.push('/notifications/add_counties');
  };

  return (
    <Stack spacing={3} sx={{ flex: 1, minHeight: 0 }}>
      <Typography>{t('notifications.description')}</Typography>

      <Box className={classes.card}>
        <List disablePadding sx={{ flex: 1, overflowY: 'auto' }}>
          {regionSettingsSections.map((regionSection) => (
            <RegionCard
              key={regionSection.title}
              title={regionSection.title}
              regionSettings={regionSection.regionSettings}
            />
          ))}
        </List>

        <Box sx={{ p: 2 }}>
          <Button
            fullWidth
            size="large"
            variant="outlined"
            color="neutral"
            onClick={handleAddMoreCounties}
          >
            {t('notifications.addMoreCounties')}
          </Button>
        </Box>
      </Box>
    </Stack>
  );
};
